import React, { useEffect } from 'react';
import { animateScroll as scroll } from 'react-scroll/modules';

import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { InfoMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { IconTypes } from '@eika/icon';

import { DispatchWithState, StringKeyValue } from '../../../actions/types';
import Loader from '../../../components/Loader/Loader';
import StepButtons from '../../../components/Steps/StepButtons';
import customHistory from '../../../customHistory';
import { CalculatorReducer } from '../../../reducers/calculator';
import { FeatureToggleReducer } from '../../../reducers/feature';
import { LoaderReducer } from '../../../reducers/loader';
import { formatDuration, formatRegNo } from '../../../util/common';
import { TrackingStep, trackEvent } from '../../../util/piwik-utils';

interface Props {
    action: {
        fetchCalculatorCache: () => DispatchWithState;
        openStep: (step: number) => void;
    };
    calculator: CalculatorReducer;
    loader: LoaderReducer;
    feature: FeatureToggleReducer;
    text: StringKeyValue;
}

const LoanInfo = ({ action, calculator, loader, feature, text }: Props): JSX.Element => {
    const { fetchCalculatorCache, openStep } = action;
    const { isLoan, isCampingCaravan, loanInfo } = calculator;

    const {
        equity,
        requestedLoanPeriodInMonths,
        loanAmountWithoutFees,
        vehiclePrice,
        vehicleRegistrationNumber,
        odometerValue,
    } = loanInfo;
    const { isFetchingCalculatorCache, hasFetchedCalculatorCache, hasFormLoanInfo } = loader;

    useEffect(() => {
        scroll.scrollToTop();
    }, []);

    useEffect(() => {
        fetchCalculatorCache();
    }, [fetchCalculatorCache]);

    if (isFetchingCalculatorCache) return <Loader title={text.loadingApplication} />;

    if (!hasFetchedCalculatorCache && (!hasFormLoanInfo || loanInfo.loanProduct === '')) {
        return (
            <div className="loan-info text-center">
                <p>{text.applicationMissingCacheId}</p>
                <StandardButton className="previous-button" onClick={() => customHistory.push('/calculator')}>
                    Gå til kalkulator
                </StandardButton>
            </div>
        );
    }

    return (
        <div className="loan-info">
            <InfoCard className="loan-info__card">
                {isLoan && (
                    <div className="loan-info__about-vehicle">
                        <h2>{text.applicationLoanInfoAboutVehicleTitle}</h2>

                        <div className="custom-list">
                            <ul>
                                <li className="list-left">{text.applicationLoanInfoRegNo}</li>
                                <li className="list-right">{formatRegNo(vehicleRegistrationNumber)}</li>
                            </ul>

                            {!isCampingCaravan && (
                                <ul>
                                    <li className="list-left">{text.applicationLoanInfoOdometerValue}</li>
                                    <li className="list-right">{ValutaFilter(odometerValue, ' ', 0)} km</li>
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                <div className="loan-info__about-loan">
                    <h2>{text.applicationLoanInfoAboutLoanTitle}</h2>

                    <div className="custom-list">
                        {!!vehiclePrice && (
                            <ul>
                                <li className="list-left">{text.applicationLoanInfoVehiclePrice}</li>
                                <li className="list-right">{ValutaFilter(vehiclePrice, ' ', 0)} kr</li>
                            </ul>
                        )}

                        {equity !== undefined && (
                            <ul>
                                <li className="list-left">{text.applicationLoanInfoEquity}</li>
                                <li className="list-right">{ValutaFilter(equity, ' ', 0)} kr</li>
                            </ul>
                        )}

                        {requestedLoanPeriodInMonths && (
                            <ul>
                                <li className="list-left">{text.applicationLoanInfoLoanPeriod}</li>
                                <li className="list-right">{formatDuration(requestedLoanPeriodInMonths)}</li>
                            </ul>
                        )}

                        {loanAmountWithoutFees && (
                            <ul>
                                <li className="list-left">{text.applicationLoanInfoLoanAmount}</li>
                                <li className="list-right">{ValutaFilter(loanAmountWithoutFees, ' ', 0)} kr</li>
                            </ul>
                        )}
                    </div>
                </div>
            </InfoCard>

            <InfoMessage icon={IconTypes.LYSPAERE_24} text={text.applicationLoanInfoComment} />

            <StepButtons
                previousChangeEvent={() => customHistory.push('/')}
                nextChangeEvent={() => {
                    trackEvent(TrackingStep.LoanInfo);
                    openStep(feature.salgspantSkipSbl ? 3 : 2);
                }}
            />
        </div>
    );
};

export default LoanInfo;
